import { Button, Flex, IconButton, Input, InputGroup, InputLeftElement, TableContainer, Tooltip } from "@chakra-ui/react";
import { IconEdit, IconPlus } from "@tabler/icons-react";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "am-tax-fe-core";
import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { TableRowProps } from "../../../utils/types";
import { CreateOrEditJurisdictionModal } from "./CreateOrEditJurisdictionModal";
import { SectionHeader } from "../../../components/SectionHeader";
import { useGetJurisdictions } from "../../../api/jurisdictions/jurisdictionQueries";
import { Jurisdiction } from "../../../api/jurisdictions/jurisdictionApi";

export const JurisdictionPanel: FunctionComponent = () => {
    const [showCreateOrEditJurisdictionDialog, setShowCreateOrEditJurisdictionDialog] = useState(false);
    const [jurisdictionData, setJurisdictionData] = useState<Jurisdiction>();
    const { data: jurisdictions = [], isLoading, refetch: refetchJurisdictions } = useGetJurisdictions();

    const columnHelper = createColumnHelper<Jurisdiction>();

    const [filteredData, setFilteredData] = useState<Jurisdiction[]>([]);

    useEffect(() => {
        setFilteredData(jurisdictions ?? []);
    }, [jurisdictions]);

    function enableEditJurisdictionModal(jurisdiction: Jurisdiction) {
        setJurisdictionData(jurisdiction);
        setShowCreateOrEditJurisdictionDialog(true);
    }

    function closeCreateOrEditJurisdictionDialog() {
        setJurisdictionData(undefined);
        setShowCreateOrEditJurisdictionDialog(false);
        refetchJurisdictions();
    }

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value.trim().toLowerCase();
        const filteredList = jurisdictions.filter(
            c =>
                c.name?.toLowerCase().includes(value.toLowerCase()) ||
                c.regulatoryLanguage?.toLowerCase().includes(value.toLowerCase()) ||
                c.introductionDescription?.toLowerCase().includes(value.toLowerCase()) ||
                (c.active ? "active".includes(value.toLowerCase()) : "inactive".includes(value.toLowerCase())),
        );
        setFilteredData(filteredList);
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Jurisdictions">
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>

                    <Input placeholder="Search Jurisdictions" onChange={handleFilter} />
                </InputGroup>
                <Button leftIcon={<IconPlus />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditJurisdictionDialog(true)}>
                    Add Jurisdiction
                </Button>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                <DataTable
                    enableFilters={false}
                    tableProps={{ size: "sm" }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No jurisdictions found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("name", {
                            header: "Name",
                            cell: (props: TableRowProps<Jurisdiction>) => props.getValue(),
                        }),
                        columnHelper.accessor("active", {
                            header: "Status",
                            cell: (props: TableRowProps<Jurisdiction>) => (props.getValue() ? "Active" : "Inactive"),
                        }),
                        columnHelper.accessor("regulatoryLanguage", {
                            header: "Regulatory Language",
                            cell: (props: TableRowProps<Jurisdiction>) => props.getValue(),
                        }),
                        {
                            id: "Edit Jurisdiction",
                            cell: (props: TableRowProps<Jurisdiction>) => {
                                const jurisdiction = props.row.original;
                                return (
                                    <Tooltip label="Edit Jurisdiction" placement="top">
                                        <IconButton
                                            icon={<IconEdit />}
                                            aria-label="Edit Jurisdiction"
                                            variant="outline"
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                enableEditJurisdictionModal(jurisdiction);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <Flex>
                <CreateOrEditJurisdictionModal
                    show={showCreateOrEditJurisdictionDialog}
                    onClose={closeCreateOrEditJurisdictionDialog}
                    jurisdictionData={jurisdictionData}
                    jurisdictionNames={jurisdictions ?? []}
                    isEdit={!!jurisdictionData}
                />
            </Flex>
        </Flex>
    );
};
